import { gql } from '@apollo/client/core';

const fragSticker = gql`fragment sticker on Sticker {
   id
   title
   slug
   url
   description
   entity
   entityId
   user {
     id
     firstname
     lastname
     avatar
   }
   organization {
    id
    name
    avatar
    email
    phone
   }
   published
   tags
   cost
   campaignType
   causes
   isCausesSearchAllowed
   nonProfitSuggestions
   maxQuantity
   maxDonation
   donationMultiplier
   impact
   eventDate
   eventDateTimeZone
   startDate
   startDateTimeZone
   endDate
   endDateTimeZone
   seen
   isSpotlight
   createdAt
   isFeatured
   location
   waiverURL
   status
   varients {
    id
    stickerId
    title
    cost
    description
    sortOrder
    isActive
   }
   confirmationEmail
   reminderEmail
   hasOptions
   quickCosts
   priceRange
   glow {
    glowStartDate
    glowEndDate
    glowColor
    glowStartDateTimeZone
    glowEndDateTimeZone
   }
   sweepstakesStickerId
   sweepstakesSticker {
    aboutTheCause
    numberOfPrizesAndWinners
    prizes
    nonProfitBeneficiary
    drawingDateTime
    drawingDateTimeTimezone
    experienceDateTime
    experienceDateTimeTimezone
    approximateRetailValue
    sweepstakesConfirmation
    sweepstakesWinnerMessage
    sweepstakesLoserMessage
    checkoutPageLegal
    entries
    entryCost
  },
  isWinnerConfirmed,
}`;

const fragSweepstakeWinners = gql`
  fragment sweepstakeWinners on SweepstakeWinners {
    id
    stickerId
    entityId
    participantName
    participantAvatar
    participantEmail
    eligible
    dateCreated
    dateUpdated
  }
`;

export const findStickers = gql`
${fragSticker}
query(
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $filter: [String]!,
  $orderBy: String,
  $orderAsc: Boolean,
  $organizationId: String
) {
  findStickers(
    take: $take,
    skip: $skip,
    searchString: $searchString,
    filter: $filter,
    orderBy: $orderBy,
    orderAsc: $orderAsc,
    organizationId: $organizationId
  ) {
     ...sticker
  }
}`;

export const findOrgStickers = gql`
${fragSticker}
query($orgId: String!) {
  findOrgStickers(orgId: $orgId) {
     ...sticker
  }
}`;

export const findApprovedOrgStickers = gql`
${fragSticker}
query($orgId: String!, $searchString: String) {
  findApprovedOrgStickers(orgId: $orgId, searchString: $searchString) {
     ...sticker
  }
}`;

export const findApprovedOrgStickersBySlug = gql`
${fragSticker}
query($slug: String!, $searchString: String) {
  findApprovedOrgStickersBySlug(slug: $slug, searchString: $searchString) {
     ...sticker
  }
}`;

export const findPublishedStickers = gql`
${fragSticker}
query($filter: [String]!, $take: Int!, $skip: Int!, $searchString: String) {
  findPublishedStickers(filter: $filter, take: $take, skip: $skip, searchString: $searchString) {
     ...sticker
  }
}`;

export const findPublishedStickersWithFiltering = gql`
${fragSticker}
query($filter: [String]!, $take: Int!, $skip: Int!, $searchString: String) {
  findPublishedStickers(filter: $filter, take: $take, skip: $skip, searchString: $searchString) {
     ...sticker
  }
}`;

export const findPublishedStickersShortDetails = gql`
query(
  $filter: [String!]!,
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $socialShopUserId: UUID
) {
  findPublishedStickersShortDetails(
    filter: $filter,
    take: $take,
    skip: $skip,
    searchString: $searchString,
    socialShopUserId: $socialShopUserId
  ) {
    id
    title
    url
    cost
    published
    entity
    campaignType
    priceRange
    quickCosts
    organization {
      name
      avatar
    }
    user {
      firstname
      lastname
      avatar
    }
  }
}`;

export const findPurchasedStickers = gql`
${fragSticker}
query($entity: String! $entityId: String!) {
  findPurchasedStickers(entity: $entity, entityId: $entityId) {
     ...sticker
   }
}`;

export const findPurchasedStickersHistory = gql`
query FindPurchasedStickersHistory(
  $entity: String!
  $entityId: String!
  $skip: Int!
  $take: Int!
  $searchString: String
) {
  findPurchasedStickersHistory(
    entityId: $entityId
    entity: $entity
    skip: $skip
    take: $take
    searchString: $searchString
  ) {
    id
    purchasePrice
    purchaseDate
    sticker {
      id
      title
      url
      campaignType
      entity
      entityId
      cost
      organization {
        id
        name
        avatar
      }
      user {
        id
        firstname
        lastname
        avatar
      }
    }
  }
}`;

export const findSticker = gql`
${fragSticker}
query($id: String!) {
   findSticker(id: $id) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const findStickerBySlug = gql`
${fragSticker}
query($slug: String!) {
   findStickerBySlug(slug: $slug) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const findOrgSticker = gql`
${fragSticker}
query($orgId: String! $id: String!) {
   findOrgSticker(orgId: $orgId, id: $id) {
     ...sticker
     creatorTitle
     glow {
       id
       title
       glowStartDate
       glowEndDate
       glowStartDateTimeZone
       glowEndDateTimeZone
       description
     }
   }
}`;

export const mutateSticker = gql`
${fragSticker}
mutation($input: StickerInput!) {
   mutateSticker(input: $input) {
     ...sticker
   }
}`;

export const sendSweepstakesLoserEmail = gql`
mutation($stickerId: UUID!) {
   sendSweepstakesLoserEmail(stickerId: $stickerId)
}`;

export const drawSweepstakesWinners = gql`
  ${fragSweepstakeWinners}
  mutation($sweepstakesStickerId: UUID!, $participantIds: [UUID!]) {
    drawSweepstakesWinners(
      sweepstakesStickerId: $sweepstakesStickerId,
      participantIds: $participantIds
    ) {
      ...sweepstakeWinners
    }
  }
`;

export const getSweepstakesWinners = gql`
  ${fragSweepstakeWinners}
    mutation($sweepstakesStickerId: UUID!) {
    getSweepstakesWinners(sweepstakesStickerId: $sweepstakesStickerId) {
      ...sweepstakeWinners
    }
  }`;

export const mutateRemoveSticker = gql`
mutation($id: String!) {
  mutateRemoveSticker(id: $id)
}`;

export const mutateOrgSticker = gql`
${fragSticker}
mutation($orgId: String!, $input: StickerInput!) {
   mutateOrgSticker(orgId: $orgId, input: $input) {
     ...sticker
   }
}`;

export const mutateRemoveOrgSticker = gql`
mutation($orgId: String!, $id: String!) {
  mutateRemoveOrgSticker(orgId: $orgId id: $id)
}`;

export const findAllSpots = gql`
query {
  findAllSpots {
    id
    title
    description
    coverMediaUrl
    coverMediaType
    thumbnailUrl
    entity
    sortOrder
    organization{
      name
      avatar
    }
    user{
      firstname
      lastname
      avatar
    }
    moodboards{
      id
      title
      metadata
      url
    }
   }
}`;

export const getCMSHomeScreenData = gql`
query {
  getCMSHomeScreenData {
    id
    orgCollectionTitle
    stickerCollectionTitle
    spotCollectionTitle
    metaCarousel {
      id
      title
      image
      order
    }
    metaFeaturedOrgs {
      id
      title
      image
      order
    }
    metaFeaturedStickers {
      id
      title
      image
      order
    }
    metaFeaturedSpots {
      id
      title
      image
      order
    }
  }
}`;

export const mutateCMSHomeScreenData = gql`
mutation($input: HomeScreenDataInput!) {
  mutateCMSHomeScreenData(input: $input) {
    id
    orgCollectionTitle
    stickerCollectionTitle
    spotCollectionTitle
    metaCarousel {
      id
      title
      image
      order
    }
    metaFeaturedOrgs {
      id
      title
      image
      order
    }
    metaFeaturedStickers {
      id
      title
      image
      order
    }
    metaFeaturedSpots {
      id
      title
      image
      order
    }
  }
}`;

export const isStickerPurchased = gql`
query($entity: String! $entityId: String! $stickerId: String!) {
  isStickerPurchased(entity: $entity, entityId: $entityId, stickerId: $stickerId)
}`;

export const mutateStickerStatus = gql`
mutation($input: [StickerStatusInput!]!) {
  mutateStickerStatus(input: $input) {
    id
    status
  }
}`;

export const mutateStickerVarient = gql`
mutation($input: StickerVarientInput!) {
  mutateStickerVarient(input: $input) {
    id
    isActive
  }
}`;

export const getStickerInsights = gql`
query($stickerId: String!) {
  getStickerInsights(stickerId: $stickerId) {
    totalRevenue
    unitsSold
    purchasedUsersCount
    purchased
    varients {
      id
      stickerId
      title
      cost
      description
      sortOrder
      isActive
      totalRevenue
      unitsSold
    }
    causeSummary {
      key
      value
    }
    benevityCausesName
  }
}`;

export const postGiftSticker = gql`
mutation($input: GiftStickerInput!) {
  giftSticker(input: $input)
}`;

export const collectFreeSticker = gql`
mutation($stickerId: String!, $socialShopId: UUID, $glowId: UUID) {
  collectFreeSticker(stickerId: $stickerId, socialShopId: $socialShopId, glowId: $glowId)
}`;

export const collectAnonymousFreeSticker = gql`
mutation(
  $stickerId: String!,
  $email: String!,
  $phone: String!,
  $firstName: String!,
  $lastName: String!,
  $embedOrganizationId: UUID,
  $embedHost: String,
  $socialShopId: UUID,
  $glowId: UUID
) {
  collectAnonymousFreeSticker(
    stickerId: $stickerId,
    email: $email,
    phone: $phone,
    firstName: $firstName,
    lastName: $lastName,
    embedOrganizationId: $embedOrganizationId,
    embedHost: $embedHost,
    socialShopId: $socialShopId,
    glowId: $glowId
  )
}`;

export const mutateSocialShopSticker = gql`
mutation($stickerId: UUID!, $orgId: String) {
  mutateSocialShopSticker(
    input: { sortOrder: 0, stickerId: $stickerId }
    orgId: $orgId
  ) {
    id
  }
}`;

export const mutateRemoveSocialShopSticker = gql`
mutation($stickerId: String!, $orgId: String) {
  mutateRemoveSocialShopSticker(stickerId: $stickerId, orgId: $orgId)
}`;

export const findPublishedStickersShortDetailsWithSocialShop = gql`
query(
  $filter: [String!]!,
  $take: Int!,
  $skip: Int!,
  $searchString: String,
  $socialShopUserId: UUID,
  $socialShopOrgId: UUID
) {
  findPublishedStickersShortDetails(
    filter: $filter,
    take: $take,
    skip: $skip,
    searchString: $searchString,
    socialShopUserId: $socialShopUserId,
    socialShopOrgId: $socialShopOrgId
  ) {
    id
    title
    url
    cost
    published
    entity
    campaignType
    priceRange
    quickCosts
    socialShop {
      id
      sortOrder
    }
    organization {
      name
      avatar
    }
    user {
      firstname
      lastname
      avatar
    }
  }
}`;
